import { Auth } from '@aws-amplify/auth'
import { Hub } from '@aws-amplify/core'
import type { HubCapsule } from '@aws-amplify/core/lib-esm/Hub'
import { useQueryClient } from '@tanstack/react-query'
import { CognitoUserSession } from 'amazon-cognito-identity-js'
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState
} from 'react'
import { useEffectOnce } from 'react-use'

import { isInDummyMode } from 'dummy/static'
import { signOut } from 'services/AuthService'
import AuthUtils from 'utils/AuthUtils'
import CommonUtils from 'utils/CommonUtils'

import PortalConfigContext, {
  PortalConfigContextState
} from './PortalConfigContext'

export interface User {
  id: string
  username: string // primary id for user
  attributes: {
    'custom:portal_user_id': string
    sub: string
    'custom:brand_id': string
    email_verified: boolean
    'custom:contact_entity_id': string
    'custom:org_id': string
    'custom:origin': string
    'custom:auth_challenge'?: string
    email: string
  }
}

const AUTH_HUB_CHANNEL = 'auth'
const HUB_CAPSULE_STATUS = {
  CONFIGURED: 'configured',
  SIGN_IN: 'signIn',
  SIGN_OUT: 'signOut'
}

export interface AuthContextState {
  data: AuthContextData
  actions: AuthContextActions
}

export interface AuthContextData {
  initialized: boolean
  authenticated: boolean
  user: User
  session: CognitoUserSession
  isAdminSession: boolean
}

export interface AuthContextActions {
  setIsAuthenticated: (authenticated: boolean) => void
  refetchUser: () => Promise<void>
}

const defaultState: AuthContextState = {
  data: null,
  actions: {
    setIsAuthenticated: () => null,
    refetchUser: () => null
  }
}

const CONFIG_USER: User = {
  id: 'id',
  username: 'username',
  attributes: {
    sub: 'sub',
    email: 'john-doe@mail.com',
    email_verified: true,
    'custom:portal_user_id': 'portal_user_id',
    'custom:brand_id': 'brand_id',
    'custom:contact_entity_id': 'contact_entity_id',
    'custom:org_id': 'org_id',
    'custom:origin': 'origin'
  }
}

const AuthContext = createContext<AuthContextState>(defaultState)

export interface AuthContextProviderProps {
  children: ReactNode
}

const AuthContextProvider = ({ children }: AuthContextProviderProps) => {
  const queryClient = useQueryClient()
  const { data: configData } =
    useContext<PortalConfigContextState>(PortalConfigContext)

  const [initialized, setInitialized] = useState(false)
  const [authenticated, setIsAuthenticated] = useState(false)
  const [user, setUser] = useState<User>(null)
  const [session, setSession] = useState<CognitoUserSession>(null)
  const [isAdminSession, setIsAdminSession] = useState<boolean>(false)
  const isDummy = isInDummyMode()

  useEffectOnce(() => {
    if (isDummy) {
      setUser(CONFIG_USER)
      setInitialized(true)
    }
  })

  // configure amplify from portal config
  useEffect(() => {
    ;(async () => {
      if (configData?.portalConfig?.cognito_details?.cognito_user_pool_id) {
        if (location.search.includes('login_as_token')) {
          await signOut().catch(console.warn)
        }

        AuthUtils.configure({
          orgId: configData.portalConfig.organization_id,
          cognitoDetails: configData.portalConfig.cognito_details
        })
      }
    })()
  }, [configData?.portalConfig])

  const refetchUser = async () => {
    try {
      const currentUser = await Auth.currentAuthenticatedUser()

      setUser(currentUser)
    } catch (error) {
      setUser(null)
    }
  }

  useEffect(() => {
    if (isDummy) {
      return null
    }

    const authListener = async (hubCapsule: HubCapsule) => {
      let cognitoUserInfo = null
      let session = null

      try {
        cognitoUserInfo = await Auth.currentUserInfo()
        session = await Auth.currentSession()
      } catch (err) {
        cognitoUserInfo = null
        session = null
      }

      const idTokenPayload = session ? session.getIdToken().payload : null

      // First configuration of amplify, set data if any
      if (hubCapsule.payload.event === HUB_CAPSULE_STATUS.CONFIGURED) {
        if (cognitoUserInfo && !CommonUtils.isJsonEmpty(cognitoUserInfo)) {
          setUser(cognitoUserInfo)
          setIsAuthenticated(true)
        }

        if (session) {
          setSession(session)
        }

        if (idTokenPayload) {
          setIsAdminSession(!!idTokenPayload['admin_user_id'])
        }

        setInitialized(true)
      }

      if (hubCapsule.payload.event === HUB_CAPSULE_STATUS.SIGN_IN) {
        // We only invalidate query data if the user is different
        setUser((currentUser) => {
          const sameUser = cognitoUserInfo.username === currentUser?.username

          if (!sameUser) {
            queryClient.removeQueries()
          }

          return cognitoUserInfo
        })

        setSession(session)
        setIsAdminSession(!!idTokenPayload['admin_user_id'])

        setIsAuthenticated(true)
      }

      if (hubCapsule.payload.event === HUB_CAPSULE_STATUS.SIGN_OUT) {
        setIsAuthenticated(false)

        setUser(null)
        setSession(null)
        setIsAdminSession(false)
      }
    }

    Hub.listen(AUTH_HUB_CHANNEL, authListener)

    return () => Hub.remove(AUTH_HUB_CHANNEL, authListener)
  }, [isDummy, queryClient])

  return (
    <AuthContext.Provider
      value={{
        data: { initialized, authenticated, user, session, isAdminSession },
        actions: { setIsAuthenticated, refetchUser }
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export { AuthContextProvider }
export default AuthContext
